import { isSelectionCopySettingsEnabled } from '@/app-shell/flags/selection-copy-settings';
import { useFindReplaceStates } from '@/atoms/findReplaceAtoms';
import { snippetModalAtom } from '@/components/modal/snippet-editor-modal-atoms';
import { convertSelectionToSection } from '@/components/tiptap/shared/convert-selection-to-section';
import { copyVMarkdownSelectionWithNoteSettings } from '@/components/tiptap/shared/copy-selection';
import { toSectionEditorContext } from '@/components/tiptap/vMarkdown/editor-type';
import { useCopySettings } from '@/features/copySettings';
import { copySettingsToWire } from '@/features/copySettings/useCopySettings.helpers';
import {
  isFormattingOff,
  writeHtmlAndMarkdownToClipboard,
} from '@/helpers/copyHelpers/copyHelpers-v2';
import { isSupersededCopyError } from '@/helpers/copyHelpers/reportCopyFailure';
import { simpleHtmlToMarkdown } from '@/helpers/htmlToMarkdown';
import { reformatHtml } from '@/helpers/markdownToHtml';
import { getSelectionWordCounts } from '@/helpers/wordCount';
import { sessionCopyClicked } from '@/lib/analytics/scribe/session';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import {
  openSnippetFromSelection,
  seedFindReplaceFromSelection,
  stripEmptySectionHeadingPresentationHtml,
  type EditorType,
} from '@heidi/editor/core';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { CopyMethod, SessionCopiedFormat } from '@heidi/session/contracts';
import { getHTMLFromFragment, type Editor } from '@tiptap/react';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

export const useTextMenuCommands = (editor: Editor, editorType?: EditorType) => {
  const intl = useIntl();
  const sessionId = useCurrentSessionId();
  const { onFindReplaceClick } = useFindReplaceStates();
  const [, setSnippetModalOpen] = useAtom(snippetModalAtom);
  const { settings, isImprovedCopyEnabled } = useCopySettings();
  const onBold = useCallback(() => editor.chain().focus().toggleBold().run(), [editor]);
  const onItalic = useCallback(
    () => editor.chain().focus().toggleItalic().run(),
    [editor]
  );

  const onUnderline = useCallback(
    () => editor.chain().focus().toggleUnderline().run(),
    [editor]
  );

  const onCodeBlock = useCallback(() => {
    const { state } = editor;
    const { from, to } = state.selection;
    const $from = state.doc.resolve(from);
    const $to = state.doc.resolve(to);

    // Single-block (or empty) selection: standard toggle semantics.
    if ($from.sameParent($to)) {
      editor.chain().focus().toggleCodeBlock().run();
      return;
    }

    // Multi-block selection: expand to whole blocks and replace with a single
    // codeBlock node. Without this, toggleCodeBlock's setBlockType converts
    // each paragraph individually, producing N adjacent <pre><code> blocks.
    const blockStart = $from.before($from.depth);
    const blockEnd = $to.after($to.depth);
    const text = state.doc.textBetween(blockStart, blockEnd, '\n', '\n');

    editor
      .chain()
      .focus()
      .insertContentAt(
        { from: blockStart, to: blockEnd },
        {
          type: 'codeBlock',
          content: text ? [{ type: 'text', text }] : undefined,
        }
      )
      .run();
  }, [editor]);

  const onSubscript = useCallback(
    () => editor.chain().focus().toggleSubscript().run(),
    [editor]
  );

  const onBulletList = useCallback(
    () => editor.chain().focus().toggleBulletList().run(),
    [editor]
  );

  const onOrderedList = useCallback(
    () => editor.chain().focus().toggleOrderedList().run(),
    [editor]
  );

  const onCopy = useCallback(async () => {
    try {
      const { from, to } = editor.state.selection;
      if (from === to) {
        toast({
          tone: 'destructive',
          title: intl.formatMessage({
            defaultMessage: 'No text selected',
            id: 'niEXfG/YKw',
          }),
        });
        return;
      }
      const selectedFragment = editor.state.doc.slice(from, to).content;
      const selectedHtml = stripEmptySectionHeadingPresentationHtml(
        getHTMLFromFragment(selectedFragment, editor.schema)
      );
      const honourSettings = isSelectionCopySettingsEnabled() && isImprovedCopyEnabled;
      if (honourSettings) {
        await copyVMarkdownSelectionWithNoteSettings(selectedHtml, settings);
      } else {
        const reformattedHtml = reformatHtml(selectedHtml);
        const markdown = simpleHtmlToMarkdown({ html: selectedHtml, isCopy: true });
        await writeHtmlAndMarkdownToClipboard({ html: reformattedHtml, markdown });
      }

      const { selectedWordCount, totalWordCount } = getSelectionWordCounts(
        editor.state.doc,
        from,
        to
      );
      sessionCopyClicked({
        format: honourSettings
          ? SessionCopiedFormat.WITH_TOGGLES
          : SessionCopiedFormat.FORMAT_WITH_LISTS,
        copyMethod: CopyMethod.CLICK,
        selectedWordCount,
        totalWordCount,
        // Editor engine — not this hook's `editorType` param, which is the
        // surface (note/template/document).
        editorType: 'block_markdown',
        copySource: 'pane_copy_button',
        htmlFlavorWritten: honourSettings ? !isFormattingOff(settings) : true,
        ...(honourSettings && { copySettings: copySettingsToWire(settings) }),
      });

      toast({
        tone: 'success',
        title: intl.formatMessage({
          defaultMessage: 'Copied to clipboard',
          id: '93oVThAHvU',
        }),
      });
    } catch (error) {
      // A desktop copy superseded by a newer one: that copy landed, so this
      // one is a silent cancellation (markdown-native twin parity).
      if (isSupersededCopyError(error)) return;
      // PHI-safe: log only metadata (session id, editor variant, error name) —
      // never the clipboard content, which carries note/transcript PHI.
      logger.error('Failed to copy vMarkdown selection to clipboard', {
        sessionId,
        editorVariant: 'vmarkdown',
        errorName: error instanceof Error ? error.name : 'unknown',
      });
      toast({
        tone: 'destructive',
        title: intl.formatMessage({
          defaultMessage: 'Failed to copy',
          id: '8Iv00QJneF',
        }),
      });
    }
  }, [editor, intl, isImprovedCopyEnabled, sessionId, settings]);

  const onFindReplace = useCallback(
    () => seedFindReplaceFromSelection(editor, onFindReplaceClick),
    [editor, onFindReplaceClick]
  );

  const onCreateSnippet = useCallback(
    () => openSnippetFromSelection(editor, setSnippetModalOpen),
    [editor, setSnippetModalOpen]
  );

  const onSectionalWriteBack = useCallback(
    ({ sectionId }: { sectionId: string }) => {
      editor.commands.toggleSectionalWriteBack(sectionId);
    },
    [editor]
  );

  const onConvertToSection = useCallback(() => {
    const converted = convertSelectionToSection(editor);
    if (!converted) {
      toast({
        tone: 'warning',
        title: intl.formatMessage({
          defaultMessage: "This selection can't be converted into a section",
          id: 'GZp0BbPvBB',
        }),
      });
      return;
    }
    track({
      event_name: TrackingEventName.SectionCreated,
      props: {
        creation_method: 'convert_selection',
        editor_context: toSectionEditorContext(editorType),
        editor_type: 'vmarkdown',
      },
      options: { amplitude: false },
    });
  }, [editor, intl, editorType]);

  return {
    onBold,
    onItalic,
    onSubscript,
    onBulletList,
    onOrderedList,
    onCopy,
    onFindReplace,
    onCreateSnippet,
    onSectionalWriteBack,
    onUnderline,
    onCodeBlock,
    onConvertToSection,
  };
};
